import React, { useEffect, useState } from 'react';
import { cn } from '../../utils/cn';
import { UNDERLYING, MONTHLY_ONLY_UNDERLYINGS, EXPIRY_TYPE_CONST } from '../../constants/tradingConstants';
import Tooltip from '../ui/Tooltip';
import { getExpiries } from '../../services/referenceDataService';
import { CHIP_DENSE } from '../../utils/chipColors';
import { computeDTE } from '../../utils/dte';

export type DteExpiryType = 'weekly' | 'monthly';

export interface DtePrefs {
  index: string;
  expiryType: DteExpiryType;
}

interface DTEControlsProps {
  value: DtePrefs;
  onChange: (next: DtePrefs) => void;
}

const STORAGE_KEY = 'dteCalendarPrefs';
const DEFAULT_PREFS: DtePrefs = { index: UNDERLYING.NIFTY, expiryType: EXPIRY_TYPE_CONST.WEEKLY };

const INDEX_OPTIONS = Object.values(UNDERLYING);

export function loadDtePrefs(): DtePrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw);
    const index = INDEX_OPTIONS.includes(parsed?.index) ? parsed.index : DEFAULT_PREFS.index;
    let expiryType: DteExpiryType = parsed?.expiryType === 'monthly' ? 'monthly' : 'weekly';
    // Force monthly for monthly-only indices
    if (MONTHLY_ONLY_UNDERLYINGS.includes(index)) expiryType = 'monthly';
    return { index, expiryType };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function saveDtePrefs(prefs: DtePrefs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage may be disabled — silently ignore
  }
}

// ── Today's IST date ─────────────────────────────────────────────────────────
// Uses formatToParts with explicit year/month/day to guarantee zero-padded
// YYYY-MM-DD output (e.g. "2026-06-03") regardless of browser/Node ICU data.
// A plain Intl.DateTimeFormat('en-CA').format() can produce un-padded output
// like "2026-6-3" on some environments, which breaks the expiry date comparison.
function todayIST(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * For every underlying, fetch its next few expiries and return the names of
 * those whose expiry falls exactly on today (DTE = 0).
 *
 * Uses computeDTE (the app's canonical DTE engine) for the comparison — more
 * robust than a raw string equality check, which can silently fail if the
 * date format has inconsistent zero-padding across environments.
 *
 * Fetches 4 expiries per underlying so there is always enough data even when
 * the API skips today's expiry and starts from the next one.
 *
 * Runs once on mount; expiry dates are stable within a trading day.
 * All calls hit the referenceDataService 1h in-memory+localStorage cache.
 */
function useTodayExpiries(): string[] {
  const [expiring, setExpiring] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    const today = todayIST();

    const checks: Promise<string | null>[] = INDEX_OPTIONS.map(async (idx) => {
      const type = MONTHLY_ONLY_UNDERLYINGS.includes(idx) ? 'monthly' : 'weekly';
      try {
        const res = await getExpiries(idx, type, 4);
        if (!res.expiries.length) return null;
        // DTE = 0 means today is an expiry day for this underlying.
        const dte = computeDTE(today, res.expiries, {});
        return dte === 0 ? idx : null;
      } catch {
        return null; // silently ignore network errors — feature is informational
      }
    });

    Promise.all(checks).then((results) => {
      if (!cancelled) {
        setExpiring(results.filter((r): r is string => r !== null));
      }
    });

    return () => { cancelled = true; };
  }, []); // expiry dates are stable intraday — no deps needed

  return expiring;
}

const DTEControls: React.FC<DTEControlsProps> = ({ value, onChange }) => {
  const monthlyOnly = MONTHLY_ONLY_UNDERLYINGS.includes(value.index);
  const todayExpiries = useTodayExpiries();

  const handleIndex = (next: string) => {
    const expiryType: DteExpiryType = MONTHLY_ONLY_UNDERLYINGS.includes(next) ? 'monthly' : value.expiryType;
    const updated = { index: next, expiryType };
    saveDtePrefs(updated);
    onChange(updated);
  };

  const handleExpiry = (next: DteExpiryType) => {
    const updated = { ...value, expiryType: next };
    saveDtePrefs(updated);
    onChange(updated);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">DTE</span>

      {/* Index selector */}
      <select
        value={value.index}
        onChange={e => handleIndex(e.target.value)}
        className="text-xs font-medium px-2 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="DTE index"
      >
        {INDEX_OPTIONS.map(idx => (
          <option key={idx} value={idx}>{idx}</option>
        ))}
      </select>

      {/* Expiry type toggle */}
      <div
        className="flex rounded-md overflow-hidden border border-gray-200 dark:border-gray-700"
        role="radiogroup"
        aria-label="DTE expiry type"
      >
        <ExpiryToggleButton
          label="Weekly"
          active={value.expiryType === 'weekly'}
          disabled={monthlyOnly}
          disabledReason={monthlyOnly ? `${value.index} has no weekly contracts — monthly only` : undefined}
          onClick={() => handleExpiry('weekly')}
        />
        <ExpiryToggleButton
          label="Monthly"
          active={value.expiryType === 'monthly'}
          onClick={() => handleExpiry('monthly')}
        />
      </div>

      {/* Expiry-today indicators — shown only on expiry day (DTE = 0) */}
      {todayExpiries.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap" title="Underlyings with expiry today">
          <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">exp</span>
          {todayExpiries.map((idx) => (
            <span
              key={idx}
              className={cn(
                'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide',
                CHIP_DENSE.EXIT_PENDING,
              )}
              title={`${idx} expires today`}
            >
              {idx}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

interface ExpiryToggleButtonProps {
  label: string;
  active: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onClick: () => void;
}

const ExpiryToggleButton: React.FC<ExpiryToggleButtonProps> = ({ label, active, disabled, disabledReason, onClick }) => {
  const button = (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'px-2.5 py-1.5 text-xs font-medium transition-colors first:border-r border-gray-200 dark:border-gray-700',
        active
          ? 'bg-blue-600 text-white'
          : disabled
          ? 'bg-gray-50 dark:bg-gray-900 text-gray-300 dark:text-gray-600 cursor-not-allowed'
          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700',
      )}
    >
      {label}
    </button>
  );
  if (disabled && disabledReason) {
    return <Tooltip text={disabledReason}>{button}</Tooltip>;
  }
  return button;
};

export default DTEControls;